import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { colors, constants } from "utils";
import { COLLAB_RUBRIC_URL, MATH_RUBRIC_URL } from "constants/rubricLinks";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import {
  FilterBar as SharedFilterBar,
  SearchableSelectDropdown,
  SelectDropdown,
} from "components/shared/FilterDropdowns/FilterDropdowns";
import { FeedbackCTA } from "components/shared/FeedbackCTA/FeedbackCTA";
import { HeadingBetaBadge } from "./BetaBadge";
import { Tab, TabList } from "components/shared/PageTabs/PageTabs";
import { GroupFocusCell } from "./GroupFocusCell";
import { HeadlineCell } from "./HeadlineCell";
import { DownloadMenu } from "./DownloadMenu";
import { RubricLink } from "./RubricLink";
import { ActivityCollaborationTrend } from "./ActivityCollaborationTrend";
import { MathProficiency } from "./MathProficiency";
import { StudentSelectionTable } from "./StudentSelectionTable";
import { ViewQuestionsModal } from "components/pages/Educator/Dashboard/ViewQuestionsModal";
import { buildPeriodsCsv, displayedPeriods } from "./trendPeriods";
import { buildCollaborationTrendPdf, buildProficiencyPdf, buildSessionsPdf } from "./sessionReportPdf";
import { buildProficiencyCsv, questionLabel, sortProficiencyRows } from "./proficiencyStandards";
import { overlayEntry } from "./studentQuestionOverlay";
// Column widths are measured from the header and cell text, so renaming a
// column re-measures it.
import { SESSION_COLUMN_WIDTHS, SESSION_TABLE_MIN_WIDTH } from "./sessionColumnWidths";
import { sortRosterRows } from "./studentRoster";
import { trackClickedButton } from "utils/analytics";
import { useLandingPageView, useViewedPageTracking } from "hooks/usePageViewTracker";
import {
  BodyRow,
  Cell,
  ColumnHeader,
  HeaderButton,
  LevelTag,
  SoloPill,
  SoloAccuracyKey,
  SortGlyph,
  SortableHeader,
  Table,
  TableWrapper,
} from "./reportTableStyles";
import {
  EM_DASH,
  SORT_KEYS,
  averageLevelText,
  buildCsv,
  csvFilename,
  dosageParts,
  isUnknownLevel,
  summaryRangeText,
  nextSortState,
  percentText,
  sessionDateParts,
  sessionReportUrl,
  sortRows,
} from "./studentReportTable";
import { pageTitleInset } from "components/pageTitleInset";

// Student Reports — one child's headline numbers and every session they were
// in, on one screen a teacher can link to, download and click into.
//
// Three tabs, all three filled. Each asks a different question of the same
// resolved sessions: what happened in each one, how the pattern moved, and
// what this child can do on each standard.

const TABS = [
  { key: "session_detail", label: "Sessions" },
  { key: "activity_collaboration_trend", label: "Dosage & Collaboration" },
  { key: "math_proficiency", label: "Proficiency" },
];

const STUDENT_REPORTS_PAGE_NAME = "Student Reports";
// Where a teacher opened a student from.
const STUDENT_SOURCE = { LIST: "Student List", DROPDOWN: "Student Dropdown" };
// Independent of the visible tab labels, so renaming a tab in the UI does not
// rename it in Mixpanel and split its history. The one exception is the middle
// tab, renamed on purpose to match its label: its events before the rename
// carry "Activity & Collaboration Trend".
const TAB_ANALYTICS_NAME = {
  session_detail: "Session Detail",
  activity_collaboration_trend: "Dosage & Collaboration",
  math_proficiency: "Math Proficiency",
};

// What a downloaded file is called after the student and the class. The
// Session Detail file keeps the bare name it shipped with, so a teacher's
// existing files do not suddenly sort somewhere else.
const TAB_FILE_SUFFIX = {
  activity_collaboration_trend: "collaboration-trend",
  math_proficiency: "math-proficiency",
};

const PDF_OPTION_LABEL = "PDF Document (.pdf)";
const CSV_OPTION_LABEL = "Comma Separated Values (.csv)";

const ALL_CLASSES_LABEL = "All Classes";
// The frame's own placeholder (4085:72098). "All Students" says what the table
// beneath is showing; "Select a student" described a table that is not there.
const ALL_STUDENTS_LABEL = "All Students";
const EMPTY_ROWS = [];

// What a chip whose question could not be read resolves to, so a 404 and an
// unreadable blob both keep their slot in the row.
const UNREADABLE_QUESTION = { question_text: null, answer_options: [], standards: [] };

// What the Proficiency tab asks with. The overlay's attempt list sends the
// same filters, so its ordinals are the chips' ordinals.
//
// Always answered_only: the service lists every standard of a chosen grade,
// which suits a class view, but for one student the blank rows bury the
// evidence.
const proficiencyParams = ({ studentId, classroomId, grade, domainCode }) => {
  const params = new URLSearchParams({ student_id: studentId, answered_only: "true" });
  if (classroomId) params.set("classroom_id", classroomId);
  if (grade) params.set("grade", grade);
  if (domainCode) params.set("domain_code", domainCode);
  return params;
};

// Which read a cached overlay question belongs to. The replay row is shared by
// the whole group while the answer on it is this student's alone, so the key is
// every value the request carries, not the row id.
const overlayQuestionKey = ({ studentId, classroomId, entry }) =>
  [studentId, classroomId || "", entry.session_id, entry.question_id].join("|");

const saveFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

const readFilters = (search) => {
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  return {
    studentId: params.get("student_id") || null,
    // "All Classes" is the DEFAULT and removes the parameter rather than
    // writing a sentinel: a sentinel is a second way to spell "absent" and the
    // two will drift.
    classroomId: params.get("classroom_id") || null,
    tab: TABS.some((entry) => entry.key === tab) ? tab : TABS[0].key,
    // The Math Proficiency tab's own two, in the URL for the same reason the
    // others are: a teacher who narrowed to one grade and sends the link is
    // sending what they were looking at.
    grade: params.get("grade") || null,
    domainCode: params.get("domain_code") || null,
  };
};

export const StudentReports = () => {
  const history = useHistory();
  const location = useLocation();
  const { studentId, classroomId, tab, grade, domainCode } = readFilters(location.search);
  // Only a student's detail has a tab bar; the selection list's view has no tab.
  useLandingPageView(STUDENT_REPORTS_PAGE_NAME, studentId ? { tab: TAB_ANALYTICS_NAME[tab] } : {});
  const { trackView } = useViewedPageTracking();

  const [classes, setClasses] = useState([]);
  const [roster, setRoster] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useState(null);
  const [trendSort, setTrendSort] = useState(null);
  const [focusLabels, setFocusLabels] = useState({});
  const [proficiency, setProficiency] = useState(null);
  const [proficiencySort, setProficiencySort] = useState(null);
  const [selection, setSelection] = useState(null);
  const [selectionSort, setSelectionSort] = useState(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectionError, setSelectionError] = useState(null);
  // The chip overlay: which proficiency row was clicked and on which chip, the
  // standard's full attempt history in time order once it has been read, the
  // position the overlay is showing, and the questions resolved so far.
  const [overlay, setOverlay] = useState(null);
  const [overlayAttempts, setOverlayAttempts] = useState(null);
  const [overlayIndex, setOverlayIndex] = useState(null);
  const [overlayQuestions, setOverlayQuestions] = useState({});
  const requestedQuestions = useRef(new Set());

  // THE FILTERS LIVE IN THE URL, not in the shared localStorage snapshot
  // (D48.1a). A student report is a thing a teacher SENDS to a colleague, and a
  // URL that does not carry the student is not a link. The snapshot would also
  // let another tab silently change WHICH CHILD this page is describing.
  const setFilters = useCallback(
    (changes) => {
      const params = new URLSearchParams(location.search);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") params.delete(key);
        else params.set(key, value);
      });
      const query = params.toString();
      history.replace(`${constants.STUDENT_REPORTS_PATH}${query ? `?${query}` : ""}`);
    },
    [history, location.search]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await request("account/educator", { method: "GET" });
        if (cancelled) return;
        setClasses(data?.classes || []);
      } catch (e) {
        if (!cancelled) logError("Failed to fetch classes for student reports", e, { category: LogCategory.ERROR });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // The roster asks for `exclude_anonymous=true`: an anonymous student has no
  // stable identity across sessions, so a per-student timeline is meaningless
  // for them — and the endpoint refuses one anyway.
  const fetchRoster = useCallback(async (scopedTo) => {
    const params = new URLSearchParams({ exclude_anonymous: "true" });
    if (scopedTo) params.set("classroom_id", scopedTo);
    try {
      const data = await request(`account/session_history/students?${params.toString()}`, {
        method: "GET",
      });
      return data || [];
    } catch (e) {
      logError("Failed to fetch students for student reports", e, { category: LogCategory.ERROR });
      return [];
    }
  }, []);

  // THE UNSCOPED ROSTER IS FETCHED ONCE AND KEPT. It is the only list that is
  // guaranteed to contain whoever the URL names, and it is what the page reads
  // the SELECTED student's name from — see `selectedStudent` below.
  useEffect(() => {
    let cancelled = false;
    fetchRoster(null).then((data) => {
      if (!cancelled) setRoster(data);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchRoster]);

  // The dropdown offers the students OF THE CHOSEN CLASS, so this list is
  // re-asked per class. With no class chosen it is the unscoped roster already
  // in hand, so returning to All Classes costs nothing.
  useEffect(() => {
    if (!classroomId) return undefined;
    let cancelled = false;
    fetchRoster(classroomId).then((data) => {
      if (!cancelled) setClassStudents(data);
    });
    return () => {
      cancelled = true;
    };
  }, [classroomId, fetchRoster]);

  // THE STRIP AND THE TABLE ARE ONE FETCH, resolved into one piece of state.
  // The easy wrong implementation — a strip fetched once on mount and a table
  // refetched per class — produces a page whose headline numbers describe the
  // child's whole year while the rows describe period 1, with nothing on
  // screen to say so. Under D48.18 the strip is the only place those averages
  // appear, so a stale strip has nothing to contradict it.
  useEffect(() => {
    if (!studentId) {
      setReport(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({ student_id: studentId });
    if (classroomId) params.set("classroom_id", classroomId);
    const query = params.toString();

    setLoading(true);
    setError(null);
    Promise.all([
      request(`student-report/summary?${query}`, { method: "GET" }),
      request(`student-report/sessions?${query}`, { method: "GET" }),
    ])
      .then(([summary, sessions]) => {
        if (cancelled) return;
        setReport({
          summary: summary || {},
          rows: sessions?.rows || EMPTY_ROWS,
          detail: sessions?.summary || {},
        });
      })
      .catch((e) => {
        if (cancelled) return;
        logError("Failed to load student report", e, { category: LogCategory.ERROR });
        setReport(null);
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studentId, classroomId, attempt]);

  // THE PROFICIENCY TAB FETCHES ONLY WHILE IT IS OPEN. It reads every replay
  // question behind every resolved session, which is the most expensive read on
  // this page; paying it for the teachers who never leave Session Detail would
  // slow down the tab they did ask for.
  //
  // Its own two filters re-fetch it and nothing else: choosing a grade must be
  // able to ADD rows, for standards of that grade the student never touched,
  // which no amount of filtering in the browser can do.
  useEffect(() => {
    if (!studentId || tab !== TABS[2].key) return undefined;

    let cancelled = false;
    const params = proficiencyParams({ studentId, classroomId, grade, domainCode });

    setLoading(true);
    setError(null);
    request(`student-report/proficiency?${params.toString()}`, { method: "GET" })
      .then((data) => {
        if (cancelled) return;
        setProficiency(data || null);
      })
      .catch((e) => {
        if (cancelled) return;
        logError("Failed to load student proficiency", e, { category: LogCategory.ERROR });
        setProficiency(null);
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studentId, classroomId, tab, grade, domainCode, attempt]);

  // The standard's full attempt history, read once per opening. The server
  // lists it newest first; the carousel walks it in time order. If the list
  // cannot be read the overlay falls back to the row's own chips.
  useEffect(() => {
    if (!overlay?.row || !studentId) return undefined;

    let cancelled = false;
    const params = proficiencyParams({ studentId, classroomId, grade, domainCode });
    params.set("row_standard_code", overlay.row.standard_code);
    request(`student-report/proficiency/attempts?${params.toString()}`, { method: "GET" })
      .then((data) => data?.attempts || [])
      .catch((e) => {
        logError("Failed to load the attempt history", e, { category: LogCategory.ERROR });
        return overlay.row.attempts || [];
      })
      .then((newestFirst) => {
        if (!cancelled) setOverlayAttempts([...newestFirst].reverse());
      });

    return () => {
      cancelled = true;
    };
  }, [overlay, studentId, classroomId, grade, domainCode]);

  // Questions are read lazily, only the shown attempt and its neighbours. A
  // failed read keeps its slot, since dropping it would shift every later
  // attempt onto a different question.
  useEffect(() => {
    if (!overlayAttempts || overlayIndex === null || !studentId) return;

    [overlayIndex, overlayIndex - 1, overlayIndex + 1]
      .map((position) => overlayAttempts[position])
      .filter((entry) => entry && !requestedQuestions.current.has(overlayQuestionKey({ studentId, classroomId, entry })))
      .forEach((entry) => {
        const key = overlayQuestionKey({ studentId, classroomId, entry });
        requestedQuestions.current.add(key);
        const params = new URLSearchParams({
          student_id: studentId,
          session_id: entry.session_id,
          question_id: entry.question_id,
        });
        if (classroomId) params.set("classroom_id", classroomId);
        request(`student-report/question?${params.toString()}`, { method: "GET" })
          .then((data) => data || null)
          .catch(() => UNREADABLE_QUESTION)
          .then((question) =>
            setOverlayQuestions((held) => ({ ...held, [key]: question }))
          );
      });
  }, [overlayAttempts, overlayIndex, studentId, classroomId]);

  // THE SELECTION TABLE IS ITS OWN FETCH, and only while no student is chosen.
  // It is a different question from the report — "who could I look at" rather
  // than "how is this child doing" — over a different population, so it carries
  // its own loading and error rather than borrowing the report's.
  useEffect(() => {
    if (studentId) return undefined;

    let cancelled = false;
    const params = new URLSearchParams();
    if (classroomId) params.set("classroom_id", classroomId);
    const query = params.toString();

    setSelectionLoading(true);
    setSelectionError(null);
    request(`student-report/students${query ? `?${query}` : ""}`, { method: "GET" })
      .then((data) => {
        if (cancelled) return;
        setSelection(data?.rows || EMPTY_ROWS);
      })
      .catch((e) => {
        if (cancelled) return;
        logError("Failed to load the student roster", e, { category: LogCategory.ERROR });
        setSelection(null);
        setSelectionError(e);
      })
      .finally(() => {
        if (!cancelled) setSelectionLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [studentId, classroomId, attempt]);

  const rememberFocus = useCallback((sessionId, label) => {
    setFocusLabels((current) =>
      current[sessionId] === label ? current : { ...current, [sessionId]: label }
    );
  }, []);

  const selectedClass = classes.find((entry) => entry.id === classroomId) || null;
  const students = classroomId ? classStudents : roster;

  // THE SELECTED STUDENT IS NOT LOOKED UP IN THE DROPDOWN'S LIST. That list
  // holds the students who HAVE SESSIONS in the chosen class, so in the one
  // situation the empty state exists for — this child has none in this class —
  // they are absent from it by construction. Reading the name there blanks the
  // Student control and unnames the empty state, the error state and the
  // feedback context at exactly the moment they are describing that child. The
  // unscoped roster is a superset of every class-scoped one, so it answers when
  // the narrowed list cannot.
  const selectedStudent =
    roster.find((entry) => entry.student_id === studentId) ||
    students.find((entry) => entry.student_id === studentId) ||
    null;
  const studentName = selectedStudent?.display_name || "this student";

  const rows = report?.rows || EMPTY_ROWS;
  const summary = report?.summary || {};
  const displayed = useMemo(() => sortRows(rows, sort, focusLabels), [rows, sort, focusLabels]);

  // The weeks as the Dosage & Collaboration table shows them, so its files
  // follow the table's sort.
  const trendPeriods = useMemo(
    () => displayedPeriods(displayed, trendSort),
    [displayed, trendSort]
  );

  const selectionRows = useMemo(
    () => sortRosterRows(selection || EMPTY_ROWS, selectionSort),
    [selection, selectionSort]
  );

  const proficiencyRows = useMemo(
    () => sortProficiencyRows(proficiency?.rows || EMPTY_ROWS, proficiencySort),
    [proficiency, proficiencySort]
  );

  const hasRows = displayed.length > 0;
  const showSessionsTable = !loading && hasRows && tab === TABS[0].key;
  const showEmptyState = Boolean(studentId) && !loading && !error && report && !hasRows;

  const downloadProps = (tabKey) => ({
    page_name: STUDENT_REPORTS_PAGE_NAME,
    class: classroomId,
    class_name: selectedClass?.name,
    student_name: selectedStudent?.display_name,
    tab: TAB_ANALYTICS_NAME[tabKey],
  });

  // A download's name: the student, the class, the tab's suffix if it has one.
  const reportFilename = (tabKey, extension) =>
    csvFilename({
      studentName: selectedStudent?.display_name,
      className: selectedClass?.name,
      suffix: TAB_FILE_SUFFIX[tabKey] || null,
      extension,
    });

  const downloadSessionsCsv = () => {
    trackClickedButton("Download CSV", downloadProps(TABS[0].key));
    saveFile(
      new Blob([buildCsv({ rows: displayed, summary: report?.detail, focusLabels })], {
        type: "text/csv;charset=utf-8;",
      }),
      reportFilename(TABS[0].key, "csv")
    );
  };

  const downloadSessionsPdf = async () => {
    trackClickedButton("Download PDF", downloadProps(TABS[0].key));
    try {
      const doc = await buildSessionsPdf({
        rows: displayed,
        summary,
        focusLabels,
        studentName: selectedStudent?.display_name,
        className: selectedClass?.name,
        rangeText: summaryRangeText(rows),
      });
      saveFile(doc.output("blob"), reportFilename(TABS[0].key, "pdf"));
    } catch (e) {
      logError("Failed to download the student report PDF", e, { category: LogCategory.ERROR });
    }
  };

  const sessionDownloadOptions = [
    { label: PDF_OPTION_LABEL, onSelect: downloadSessionsPdf },
    { label: CSV_OPTION_LABEL, onSelect: downloadSessionsCsv },
  ];

  const downloadTrendCsv = () => {
    trackClickedButton("Download CSV", downloadProps(TABS[1].key));
    saveFile(
      new Blob([buildPeriodsCsv({ periods: trendPeriods })], { type: "text/csv;charset=utf-8;" }),
      reportFilename(TABS[1].key, "csv")
    );
  };

  const downloadTrendPdf = async () => {
    trackClickedButton("Download PDF", downloadProps(TABS[1].key));
    try {
      const doc = await buildCollaborationTrendPdf({
        periods: trendPeriods,
        summary,
        studentName: selectedStudent?.display_name,
        className: selectedClass?.name,
        rangeText: summaryRangeText(rows),
      });
      saveFile(doc.output("blob"), reportFilename(TABS[1].key, "pdf"));
    } catch (e) {
      logError("Failed to download the collaboration trend PDF", e, { category: LogCategory.ERROR });
    }
  };

  const trendDownloadOptions = [
    { label: PDF_OPTION_LABEL, onSelect: downloadTrendPdf },
    { label: CSV_OPTION_LABEL, onSelect: downloadTrendCsv },
  ];

  // The Proficiency files are built from the rows as the table displays them,
  // so they follow its filters and sort.
  const downloadProficiencyCsv = () => {
    trackClickedButton("Download CSV", downloadProps(TABS[2].key));
    saveFile(
      new Blob([buildProficiencyCsv(proficiencyRows)], { type: "text/csv;charset=utf-8;" }),
      reportFilename(TABS[2].key, "csv")
    );
  };

  const downloadProficiencyPdf = async () => {
    trackClickedButton("Download PDF", downloadProps(TABS[2].key));
    try {
      const doc = await buildProficiencyPdf({
        rows: proficiencyRows,
        summary,
        studentName: selectedStudent?.display_name,
        className: selectedClass?.name,
        rangeText: summaryRangeText(rows),
        grade,
        domainName: proficiency?.filters?.domains?.find((entry) => entry.code === domainCode)?.name,
      });
      saveFile(doc.output("blob"), reportFilename(TABS[2].key, "pdf"));
    } catch (e) {
      logError("Failed to download the proficiency PDF", e, { category: LogCategory.ERROR });
    }
  };

  const proficiencyDownloadOptions = [
    { label: PDF_OPTION_LABEL, onSelect: downloadProficiencyPdf },
    { label: CSV_OPTION_LABEL, onSelect: downloadProficiencyCsv },
  ];

  // A chip opens its question. Chips carry SessionReplayQuestion row ids, not
  // question-bank ids, so attempts are resolved through
  // `/student-report/question` and handed to the modal as an explicit list.
  const openQuestion = (attempt, row) => {
    setOverlayAttempts(null);
    setOverlayIndex(null);
    setOverlay({ row, chip: attempt });
  };

  const closeQuestion = () => {
    setOverlay(null);
    setOverlayAttempts(null);
    setOverlayIndex(null);
  };

  // The overlay opens on the clicked chip's own attempt: the entry whose replay
  // row and ordinal match it.
  const overlayOpeningIndex = Math.max(
    (overlayAttempts || []).findIndex(
      (entry) => entry.question_id === overlay?.chip?.question_id && entry.ordinal === overlay?.chip?.ordinal
    ),
    0
  );

  // Picking a student is a filter that adds no history entry, so going back
  // clears the filter rather than calling `history.goBack()`. The class is
  // kept; `grade` and `domain_code` are computed from this student's attempts
  // and would not carry over meaningfully to the next one.
  // Opening a student is the same route with a filter set, so the route
  // tracker cannot see it: the drill-in click and the view it opens are sent here.
  const viewStudent = (student, source) => {
    trackClickedButton("View Student", {
      page_name: STUDENT_REPORTS_PAGE_NAME,
      source,
      class: classroomId,
      class_name: selectedClass?.name,
      student_name: student.display_name,
    });
    trackView(STUDENT_REPORTS_PAGE_NAME, { tab: TAB_ANALYTICS_NAME[TABS[0].key] });
    setFilters({ student_id: student.student_id });
  };

  const clearStudent = () =>
    setFilters({ student_id: null, grade: null, domain_code: null, tab: null });

  // A click only: the selection list it returns to is not a new page view.
  const backToMainPage = () => {
    trackClickedButton("Back to Main Page", {
      page_name: STUDENT_REPORTS_PAGE_NAME,
      tab: TAB_ANALYTICS_NAME[tab],
      student_name: selectedStudent?.display_name,
    });
    clearStudent();
  };

  const openSessionReport = (row) => {
    trackClickedButton("View Session Report", {
      page_name: STUDENT_REPORTS_PAGE_NAME,
      session_id: row.session_id,
      student_name: selectedStudent?.display_name,
      class: classroomId,
    });
    window.open(
      sessionReportUrl({ sessionId: row.session_id, studentId, classroomId }),
      "_blank",
      "noopener,noreferrer"
    );
  };

  // The list the modal walks: one entry per attempt, in time order, each either
  // resolved, pending or explicitly unavailable.
  const overlayEntries = useMemo(
    () =>
      (overlayAttempts || []).map((entry) =>
        overlayEntry({
          label: questionLabel(entry),
          question: overlayQuestions[overlayQuestionKey({ studentId, classroomId, entry })],
          studentName: selectedStudent?.display_name || null,
        })
      ),
    [overlayAttempts, overlayQuestions, selectedStudent, studentId, classroomId]
  );

  const sortHeader = (column, label, options = {}) => (
    <SortableHeader scope="col" id={options.id}>
      <HeaderButton
        type="button"
        $centered
        onClick={() => setSort(nextSortState(sort, column))}
      >
        <span>{label}</span>
        <SortGlyph direction={sort?.column === column ? sort.direction : null} />
      </HeaderButton>
    </SortableHeader>
  );

  return (
    <Container>
      {/* One header, two shapes. Picking a student does not navigate to a new
          page -- it is the same route with a filter set -- so the frames'
          difference (4085:72088 without the back link, 4485:72281 with it and
          the child's name in the title) is expressed here, not by a route. */}
      <PageHeader>
        {studentId && (
          <BackButton type="button" onClick={backToMainPage}>
            <BackIcon icon={faChevronLeft} aria-hidden />
            Back to Main Page
          </BackButton>
        )}
        <HeaderContent>
          <Title>
            {studentId
              ? `${constants.STUDENT_REPORTS}: ${selectedStudent?.display_name || EM_DASH}`
              : constants.STUDENT_REPORTS}
          </Title>
          <HeadingBetaBadge>BETA</HeadingBetaBadge>
        </HeaderContent>
      </PageHeader>

      <ContentArea>
        {/* The two dropdowns belong to the SELECTION screen (4085:72089).
            Once a student is picked the detail frames drop them entirely and
            the header's "Back to Main Page" is how a teacher gets out. */}
        {!studentId && (
          <SelectionFilterBar>
            <SelectDropdown
              label="Class"
              value={classroomId}
              displayText={selectedClass?.name || ALL_CLASSES_LABEL}
              options={[
                { value: null, label: ALL_CLASSES_LABEL },
                ...classes.map((entry) => ({ value: entry.id, label: entry.name })),
              ]}
              onChange={(value) => setFilters({ classroom_id: value })}
            />
            <SearchableSelectDropdown
              label="Student"
              value={studentId}
              displayText={selectedStudent?.display_name || ALL_STUDENTS_LABEL}
              options={students.map((entry) => ({
                value: entry.student_id,
                label: entry.display_name || "Unknown Student",
              }))}
              onChange={(value) =>
                viewStudent(
                  students.find((entry) => entry.student_id === value) || { student_id: value },
                  STUDENT_SOURCE.DROPDOWN
                )
              }
            />
          </SelectionFilterBar>
        )}

        {studentId && (
          <KpiStrip
            loading={loading}
            summary={summary}
            hasStudent
            rangeText={summaryRangeText(rows)}
          />
        )}

        {studentId && (
          <TabList role="tablist" aria-label="Student report views">
            {TABS.map((entry) => (
              <Tab
                key={entry.key}
                role="tab"
                type="button"
                id={`tab-${entry.key}`}
                aria-controls={`panel-${entry.key}`}
                aria-selected={entry.key === tab}
                $active={entry.key === tab}
                onClick={() => {
                  if (entry.key !== tab) {
                    trackView(STUDENT_REPORTS_PAGE_NAME, {
                      tab: TAB_ANALYTICS_NAME[entry.key],
                    });
                  }
                  setFilters({ tab: entry.key === TABS[0].key ? null : entry.key });
                }}
              >
                {entry.label}
              </Tab>
            ))}
          </TabList>
        )}

        {/* THE FOUR STATES ARE THE TAB'S, NOT THE TABLE'S. "Choose a student",
            "we could not load this", the skeletons and "no sessions yet" are the
            same four answers whichever tab is open — and a second copy of them
            per tab is a second copy that drifts. Only the CONTENT below them
            differs. */}
        <Panel role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
          <>
              {!studentId && (
                <>
                  {selectionLoading && <RowSkeletons />}

                  {!selectionLoading && selectionError && (
                    <StateBlock data-testid="roster-error-state" $error>
                      <StateHeading>We could not load your students</StateHeading>
                      <StateText>Something went wrong on our side. Your filters have been kept.</StateText>
                      <RetryButton type="button" onClick={() => setAttempt((n) => n + 1)}>
                        Try again
                      </RetryButton>
                    </StateBlock>
                  )}

                  {/* NOT a blank table. Student Reports follows a child across
                      sessions, which needs a child who is the same person in
                      each one — so students who joined by group code without
                      signing in are deliberately absent. For many teachers
                      that is everyone, and an unexplained empty table reads as
                      a broken page rather than as the answer it is. */}
                  {!selectionLoading && !selectionError && selectionRows.length === 0 && (
                    <StateBlock data-testid="roster-empty-state">
                      <StateHeading>
                        {selectedClass
                          ? `No students to report on in ${selectedClass.name}`
                          : "No students to report on yet"}
                      </StateHeading>
                      <StateText>
                        Student Reports follows one student across their sessions, so it can only
                        show students who sign in — with Google, Clever or ClassLink. Students who
                        join with just a group code are not the same person from one session to the
                        next, so there is nothing to follow.
                        {selectedClass
                          ? ` Try ${ALL_CLASSES_LABEL} — students often run sessions with more than one of your classes.`
                          : ""}
                      </StateText>
                    </StateBlock>
                  )}

                  {!selectionLoading && !selectionError && selectionRows.length > 0 && (
                    <StudentSelectionTable
                      rows={selectionRows}
                      resetKey={`${classroomId || ""}|${selectionSort?.column || ""}|${selectionSort?.direction || ""}`}
                      sort={selectionSort}
                      onSortChange={setSelectionSort}
                      onSelect={(row) => viewStudent(row, STUDENT_SOURCE.LIST)}
                    />
                  )}
                </>
              )}

              {studentId && error && (
                <StateBlock data-testid="error-state" $error>
                  <StateHeading>We could not load this student's sessions</StateHeading>
                  <StateText>Something went wrong on our side. Your filters have been kept.</StateText>
                  <RetryButton type="button" onClick={() => setAttempt((n) => n + 1)}>
                    Try again
                  </RetryButton>
                </StateBlock>
              )}

              {studentId && !error && (
                <>
                  {loading && <RowSkeletons />}

                  {showEmptyState && (
                    <StateBlock data-testid="empty-state">
                      <StateHeading>{`No sessions yet for ${studentName}`}</StateHeading>
                      <StateText>
                        {selectedClass
                          ? `${studentName} has not completed a session in ${selectedClass.name}. Try ${ALL_CLASSES_LABEL} — students often run sessions with more than one of your classes.`
                          : `${studentName} has not completed a session yet.`}
                      </StateText>
                    </StateBlock>
                  )}

                  {!loading && hasRows && tab === TABS[1].key && (
                    <ActivityCollaborationTrend
                      rows={displayed}
                      studentId={studentId}
                      classroomId={classroomId}
                      downloadSlot={<DownloadMenu options={trendDownloadOptions} />}
                      sort={trendSort}
                      onSortChange={setTrendSort}
                    />
                  )}

                  {!loading && hasRows && tab === TABS[2].key && (
                    <MathProficiency
                      rows={proficiencyRows}
                      distribution={proficiency?.distribution}
                      filters={proficiency?.filters}
                      grade={grade}
                      domainCode={domainCode}
                      onGradeChange={(value) => setFilters({ grade: value })}
                      onDomainChange={(value) => setFilters({ domain_code: value })}
                      sort={proficiencySort}
                      onSortChange={setProficiencySort}
                      onQuestionClick={openQuestion}
                      downloadSlot={<DownloadMenu options={proficiencyDownloadOptions} />}
                    />
                  )}

                  {showSessionsTable && (
                    <SessionsToolbar data-testid="sessions-toolbar">
                      <RubricLinks>
                        <ViewLabel>View:</ViewLabel>
                        <RubricLink href={MATH_RUBRIC_URL} variant="toolbar">
                          Math Learning Rubric
                        </RubricLink>
                        <RubricLink href={COLLAB_RUBRIC_URL} variant="toolbar">
                          Collaboration Rubric
                        </RubricLink>
                      </RubricLinks>
                      <DownloadMenu options={sessionDownloadOptions} />
                    </SessionsToolbar>
                  )}

                  {showSessionsTable && (
                    <SessionsKey>
                      <SoloAccuracyKey />
                    </SessionsKey>
                  )}

                  {showSessionsTable && (
                    <TableWrapper>
                      <SessionTable>
                        {/* The frame's BODY widths (4729:69708). Its header
                            row is laid out on a different grid -- the
                            Proficiency group is 552 wide over 448 of cells --
                            so the cells are the measurement, not the header. */}
                        <colgroup>
                          {SESSION_COLUMN_WIDTHS.map((width, index) => (
                            <col key={index} style={width ? { width } : undefined} />
                          ))}
                        </colgroup>
                        <thead>
                          <tr>
                            {sortHeader(SORT_KEYS.SESSION_DATE, "Session Date", {
                              id: "col-session-date",
                            })}
                            {sortHeader(SORT_KEYS.GROUP_FOCUS, "Group Focus", {
                              id: "col-group-focus",
                            })}
                            {sortHeader(SORT_KEYS.SOLO_TIME, "Solo Accuracy", {
                              id: "col-solo-time",
                            })}
                            {/* Prose does not sort. */}
                            <CenteredColumnHeader scope="col" id="col-math-headline">
                              Math Headline
                            </CenteredColumnHeader>
                            {sortHeader(SORT_KEYS.COLLAB_LEVEL, "Collaboration Level", {
                              id: "col-collab-level",
                            })}
                            <CenteredColumnHeader scope="col" id="col-collab-headline">
                              Collaboration Headline
                            </CenteredColumnHeader>
                          </tr>
                        </thead>
                        <tbody>
                          {displayed.map((row) => {
                            const { date } = sessionDateParts(row.started_at);
                            return (
                              <BodyRow
                                key={row.session_id}
                                data-testid="session-row"
                                onClick={(event) => {
                                  // A headline's tooltip renders in a portal,
                                  // and React still bubbles its clicks here.
                                  if (event.currentTarget.contains(event.target)) {
                                    openSessionReport(row);
                                  }
                                }}
                              >
                                <Cell headers="col-session-date">
                                  <RowLink
                                    href={sessionReportUrl({
                                      sessionId: row.session_id,
                                      studentId,
                                      classroomId,
                                    })}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    {date}
                                  </RowLink>
                                </Cell>
                                <FocusCell headers="col-group-focus" data-testid="group-focus-cell">
                                  <Clamp>
                                    <GroupFocusCell row={row} onResolve={rememberFocus} />
                                  </Clamp>
                                </FocusCell>
                                <PillCell headers="col-solo-time">
                                  <SoloPill row={row} />
                                </PillCell>
                                <Cell headers="col-math-headline">
                                  <HeadlineCell
                                    text={row.student_math_rationale}
                                    onOpen={() => openSessionReport(row)}
                                  />
                                </Cell>
                                <PillCell headers="col-collab-level">
                                  {isUnknownLevel(row.student_collab_level) ? (
                                    EM_DASH
                                  ) : (
                                    <LevelTag level={row.student_collab_level} />
                                  )}
                                </PillCell>
                                <Cell headers="col-collab-headline">
                                  <HeadlineCell
                                    text={row.student_collab_rationale}
                                    onOpen={() => openSessionReport(row)}
                                  />
                                </Cell>
                              </BodyRow>
                            );
                          })}
                        </tbody>
                        {/* NO SUMMARY ROW (D48.18). The three averages are in
                            the strip above, over the identical session set; a
                            footer would print them again three inches lower. */}
                      </SessionTable>
                    </TableWrapper>
                  )}
                </>
              )}
          </>
        </Panel>

        {/* Opens over the current tab; closing it leaves the tab's filters,
            sort and scroll untouched. */}
        {overlay && overlayAttempts && (
          <ViewQuestionsModal
            isOpen
            studentResults
            onClose={closeQuestion}
            questions={overlayEntries}
            initialIndex={overlayOpeningIndex}
            onIndexChange={setOverlayIndex}
            standardCode={overlay.row?.standard_code}
            standardName={overlay.row?.standard_name}
            studentName={selectedStudent?.display_name || null}
          />
        )}

        <Footer>
          <FeedbackCTA
            reportLocation={constants.STUDENT_REPORTS}
            contextMessage={`[${constants.STUDENT_REPORTS}] Student: ${studentName} (ID: ${studentId || "N/A"}), Class: ${selectedClass?.name || ALL_CLASSES_LABEL}`}
          />
        </Footer>
      </ContentArea>
    </Container>
  );
};

/**
 * The four tiles: a value and its label, as the design draws them.
 *
 * The denominators each average was measured over were shown beneath the value
 * until the page was brought to the design, which has no room for them. They
 * are NOT merely hidden — a denominator behind a hover is unreachable by
 * keyboard and by touch, which is what the original decision rejected. They
 * survive in the CSV, where "Level 2.1 over three of five sessions" and "over
 * five of five" stay distinguishable.
 */
const KpiStrip = ({ loading, summary, hasStudent, rangeText }) => {
  const total = summary?.total_sessions || 0;
  const dosage = hasStudent ? dosageParts(summary?.total_minutes) : null;
  const tiles = [
    { key: "sessions", label: "Sessions", value: hasStudent ? String(total) : EM_DASH },
    { key: "minutes", label: "Dosage", parts: dosage, value: dosage ? null : EM_DASH },
    { key: "solo", label: "Solo Accuracy", value: percentText(summary?.solo_accuracy_pct) },
    {
      key: "collaboration",
      label: "Avg. Collaboration",
      value: averageLevelText(summary?.average_collab_level),
    },
  ];

  return (
    <Strip>
      <StripLead>
        <StripLeadTitle>Summary:</StripLeadTitle>
        {/* Dropped rather than faked when there are no sessions to bound: a
            range is a claim about a window, and "- Today" alone claims one
            that starts nowhere. */}
        {rangeText && <StripLeadRange>{rangeText}</StripLeadRange>}
      </StripLead>
      {tiles.map((entry) =>
        loading ? (
          <Tile key={entry.key} data-testid="kpi-skeleton">
            <Skeleton />
          </Tile>
        ) : (
          <Tile
            key={entry.key}
            role="group"
            data-testid={`kpi-${entry.key}`}
            aria-label={`${entry.label}: ${entry.parts ? dosageLabel(entry.parts) : entry.value}`}
          >
            <TileValue data-testid="tile-value">
              {entry.parts
                ? entry.parts.map((part, index) => (
                    <React.Fragment key={part.unit}>
                      {index > 0 ? " " : null}
                      {part.value}
                      <TileUnit>{part.unit}</TileUnit>
                    </React.Fragment>
                  ))
                : entry.value}
            </TileValue>
            <TileLabel>{entry.label}</TileLabel>
          </Tile>
        )
      )}
    </Strip>
  );
};

/** What a screen reader is told the Dosage tile says, spaced as it reads. */
const dosageLabel = (parts) => parts.map((part) => `${part.value}${part.unit}`).join(" ");

const RowSkeletons = () => (
  <TableWrapper>
    {[0, 1, 2].map((index) => (
      <SkeletonRow key={index} data-testid="row-skeleton">
        <Skeleton />
      </SkeletonRow>
    ))}
  </TableWrapper>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 92px);
  background-color: ${colors.milkGlass200};
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  background-color: ${colors.nightlyWoods500};
  padding: 24px 32px 24px ${pageTitleInset};
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

// No padding of its own: each band below it carries the frame's inset, and
// they differ -- 32px for the filter row, the strip and the body, 24px for the
// tab strip, whose rule has to run the full width beneath the tabs.
const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
`;

// The shared bar's 0 16px / 24px gap, brought to the selection frame's
// 24px 32px / 16px (4485:72089). Overridden here rather than in the shared
// component, which other report pages lay out to their own frames.
const SelectionFilterBar = styled(SharedFilterBar)`
  padding: 24px 32px;
  gap: 16px;
  border-radius: 0;

  /* 250px each, not a share of the row: the frame's dropdowns keep their width
     and leave the rest of the row empty. */
  & > * {
    flex: 0 0 250px;
    width: 250px;
  }

  /* The frame's dropdown (4485:72092) is 63px: a 17px label, a 6px gap and a
     40px select whose 1px border sits INSIDE the 40. The shared control draws a 21px label and a 42px trigger, which pushes the
     table 6px down; trimmed here, for this bar only. */
  label {
    line-height: 17px;
  }

  button[aria-haspopup] {
    height: 40px;
    box-sizing: border-box;
  }
`;

// The feedback link sits 32px in, like the body above it. The shared button
// pads itself 12px, so the band supplies the other 20.
const Footer = styled.div`
  padding: 0 20px 32px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding-left: 8px;
  padding-right: 16px;
  border: none;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: #ffffff;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const BackIcon = styled(FontAwesomeIcon)`
  width: 24px;
  height: 24px;
  margin-right: 4px;
`;

// Four tiles sharing the row equally, to the frame's own measurements
// (node 4485:72287). They shrink rather than wrap: a wrapped fifth line under
// a 1064px strip is not a layout the design has, and `min-width: 0` is what
// lets a long label narrow instead of forcing the row wider than its frame.
const Strip = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px;
`;

const StripLead = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 0 0 auto;
  white-space: nowrap;
  color: ${colors.nightlyWoods500};
`;

const StripLeadTitle = styled.span`
  font-family: "ABC Marfa", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
`;

const StripLeadRange = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const Tile = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  padding: 16px 24px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0px 1px 2px rgba(14, 26, 49, 0.06),
    0px 1px 3px rgba(14, 26, 49, 0.1);
`;

const TileValue = styled.span`
  font-family: "ABC Marfa", sans-serif;
  font-size: 32px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 0.25px;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.nightlyWoods500};
`;

// The unit rides smaller than the number it follows, inside the same line box.
const TileUnit = styled.span`
  font-size: 24px;
`;

const TileLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;

const Skeleton = styled.div`
  height: 14px;
  width: 100%;
  min-width: 140px;
  border-radius: 4px;
  background: linear-gradient(90deg, #efebe4 25%, #f7f4ef 50%, #efebe4 75%);
`;

const SkeletonRow = styled.div`
  padding: 14px 12px;
  border-bottom: 1px solid ${colors.milkGlass400 || "#E4E0D9"};
  background: #ffffff;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 32px 32px 16px;
`;


// 24px below the rubric row and 16px above the table, as the frame spaces
// them. Panel is a flex column, so these margins add to its 12px gap rather
// than collapsing: 12 + 12 above, 4 + 12 below.
const SessionsKey = styled.div`
  margin: 12px 0 4px;
`;

// The headlines do not sort, but their labels sit on the cell's centre line
// like every other header in the frame.
const CenteredColumnHeader = styled(ColumnHeader)`
  text-align: center;
`;

// The row between the tabs and the table: the rubrics on the left, Download on
// the right (frame 4729:69708). Outside TableWrapper, so it does not scroll
// sideways with a table wider than the viewport.
const SessionsToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const RubricLinks = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const ViewLabel = styled.span`
  font-weight: 700;
  color: ${colors.nightlyWoods500};
`;

const RowLink = styled.a`
  color: ${colors.nightlyWoods900};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SessionTable = styled(Table)`
  table-layout: fixed;

  /* Without a minimum the table always matched its wrapper, so the wrapper's
     horizontal scroll never engaged and narrow viewports squeezed the flexible
     Headline columns to nothing. The floor is derived from the columns. */
  min-width: ${SESSION_TABLE_MIN_WIDTH}px;

  th {
    white-space: normal;
  }

  /* A wrapped label stays flush left, as "Session / Date" is in the frame; a
     button centres its text by default. The sort icon sits at the cell's
     right edge. */
  th button {
    text-align: left;
    justify-content: space-between;
  }
`;

// The two pill columns centre their pill; prose stays flush left.
const PillCell = styled(Cell)`
  text-align: center;
`;

const FocusCell = styled(Cell)`
  padding: 16px;
`;

// Three lines, then an ellipsis.
const Clamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StateBlock = styled.div`
  padding: 44px;
  text-align: center;
  background: #ffffff;
  border-radius: 8px;
  border-left: ${(props) => (props.$error ? "4px solid #B4145A" : "none")};
`;

const StateHeading = styled.h2`
  margin: 0 0 6px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${colors.nightlyWoods900};
`;

const StateText = styled.p`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.milkGlass950};
`;

const RetryButton = styled.button`
  margin-top: 14px;
  padding: 9px 16px;
  border-radius: 6px;
  border: 1px solid ${colors.blue500};
  background: #ffffff;
  color: ${colors.blue500};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;
